import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { Faq, FaqListQuery, FaqListResult, FaqPagination, FaqPayload } from '../types/faq';

const BASE = '/admin/v1/faqs';

/** be-catalog 계약: admin write 는 Idempotency-Key 헤더 필요. */
const writeHeaders = (method: string, path: string, body: unknown): HeadersInit => ({
  'Idempotency-Key': idempotencyKeyFor(method, path, body),
});

interface ListEnvelope {
  success: boolean;
  data?: Faq[] | null;
  meta?: { pagination?: FaqPagination } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: Faq | null;
}

export const faqsQueryKey = (query: FaqListQuery) =>
  ['admin', 'faqs', query.page, query.size, query.keyword ?? '', query.categoryId ?? null] as const;

export const faqQueryKey = (faqId: number) => ['admin', 'faq', faqId] as const;

/**
 * BE 페이지네이션 계약 (live stack 확인 2026-05-30): 요청 `page` 는 Spring Pageable
 * 0-based, 응답 `meta.pagination.page` 는 1-based 로 돌아온다 (policies 와 동일).
 * UI 1-based 페이지를 0-based 로 변환한다 — 미변환 시 1페이지가 빈 응답으로 보였다.
 */
const buildSearchParams = (query: FaqListQuery): string => {
  const params = new URLSearchParams({
    page: String(Math.max(0, query.page - 1)),
    size: String(query.size),
    sort: 'sortOrder,ASC',
  });
  if (query.keyword && query.keyword.trim()) params.set('keyword', query.keyword.trim());
  if (query.categoryId != null) params.set('categoryId', String(query.categoryId));
  return params.toString();
};

const fetchFaqs = async (query: FaqListQuery): Promise<FaqListResult> => {
  const res = await adminFetch(`${BASE}?${buildSearchParams(query)}`);
  if (!res.ok) throw new Error(`faqs list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  const items = env.data ?? [];
  const pagination: FaqPagination = env.meta?.pagination ?? {
    page: query.page,
    size: query.size,
    totalItems: items.length,
    totalPages: 1,
  };
  return { items, pagination };
};

const fetchFaq = async (faqId: number): Promise<Faq> => {
  const res = await adminFetch(`${BASE}/${faqId}`);
  if (!res.ok) throw new Error(`faq fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const useFaqsQuery = (query: FaqListQuery) =>
  useQuery<FaqListResult>({
    queryKey: faqsQueryKey(query),
    queryFn: () => fetchFaqs(query),
  });

export const useFaqQuery = (faqId: number | null) =>
  useQuery<Faq>({
    queryKey: faqQueryKey(faqId ?? -1),
    queryFn: () => fetchFaq(faqId as number),
    enabled: faqId != null,
  });

export const useFaqMutation = () => {
  const qc = useQueryClient();
  const invalidateList = () => qc.invalidateQueries({ queryKey: ['admin', 'faqs'] });

  const create = useMutation({
    mutationFn: async (payload: FaqPayload): Promise<Faq> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: writeHeaders('POST', BASE, payload),
        body: JSON.stringify(payload),
      });
      // 저장 실패는 BE error.code·message 를 살려 던진다(예: base64 이미지 400 사유를 그대로 안내).
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({ faqId, payload }: { faqId: number; payload: FaqPayload }): Promise<Faq> => {
      const path = `${BASE}/${faqId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: writeHeaders('PUT', path, payload),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (_, { faqId }) => {
      void qc.invalidateQueries({ queryKey: faqQueryKey(faqId) });
      invalidateList();
    },
  });

  const remove = useMutation({
    mutationFn: async (faqId: number): Promise<void> => {
      const path = `${BASE}/${faqId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: writeHeaders('DELETE', path, ''),
      });
      if (!res.ok) throw new Error(`faq delete failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  return { create, update, remove };
};
